"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Radio } from "lucide-react";
import { liveSessionUrl } from "@/lib/live-rejoin";

type ActiveSession = {
  sessionId: string;
  role: "master" | "slave";
  setlistName: string | null;
  code: string;
};

const STORAGE_KEY = "opuslouange-bubble-pos";

function loadPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { x: number; y: number };
  } catch {
    return null;
  }
}

function savePos(x: number, y: number) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ x, y })); } catch { /* ignore */ }
}

export function LiveSessionBubble() {
  const pathname = usePathname();
  const router = useRouter();
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const didDrag = useRef(false);

  const isOnLive = pathname.startsWith("/live");

  useEffect(() => {
    if (isOnLive) return;
    fetch("/api/sessions/mine", { cache: "no-store" })
      .then((r) => r.ok ? r.json() : { data: null })
      .then(({ data }) => setSession(data));
  }, [isOnLive, pathname]);

  useEffect(() => {
    // Nécessaire : window/localStorage n'existent pas côté serveur, la position
    // par défaut ne peut être calculée qu'après le montage.
    if (!pos) {
      const saved = loadPos();
      if (saved) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPos(saved);
      } else {
        setPos({ x: window.innerWidth - 160, y: window.innerHeight - 72 });
      }
    }
  }, [pos]);

  function handlePointerDown(e: React.PointerEvent) {
    if (!bubbleRef.current) return;
    dragging.current = true;
    didDrag.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
    const rect = bubbleRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    bubbleRef.current.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!dragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (!didDrag.current && Math.hypot(dx, dy) < 8) return;
    didDrag.current = true;
    const x = Math.max(0, Math.min(window.innerWidth - 140, e.clientX - dragOffset.current.x));
    const y = Math.max(0, Math.min(window.innerHeight - 48, e.clientY - dragOffset.current.y));
    setPos({ x, y });
  }

  function handlePointerUp() {
    if (!dragging.current) return;
    dragging.current = false;
    if (pos) savePos(pos.x, pos.y);
  }

  function handleClick() {
    if (didDrag.current) return;
    if (!session) return;
    router.push(liveSessionUrl(session.sessionId, session.role));
  }

  if (isOnLive || !session || !pos) return null;

  return (
    <button
      ref={bubbleRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
      style={{ left: pos.x, top: pos.y }}
      className="fixed z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-red-500 text-white shadow-lg text-sm font-medium cursor-pointer select-none touch-none hover:brightness-110 transition-filter"
      aria-label="Retourner au live"
    >
      <Radio className="w-3.5 h-3.5 shrink-0 animate-pulse" />
      <span className="max-w-[220px] truncate">{session.setlistName ?? "Live en cours"}</span>
    </button>
  );
}
